import { Controller } from 'stimulus';

/**
 * Auto-saves billing sheet fields on change with debounce.
 *
 * Iteration 6 removed the inline invoice-sent checkbox in favour of the
 * per-row Mark as invoiced / Return to CSM endpoints. With that gone there's
 * no in-page widget that flips fields between editable / readonly, so the
 * d-none state is baked in server-side from `sheet.invoice_sent?` at render
 * time. This controller is now just the debounced PATCH plumbing for the
 * remaining editable fields.
 *
 * @target editableField - text/number inputs and textareas that auto-save
 * @value url - the PATCH endpoint URL
 * @value sheetId - the MonthlyBillingSheet ID
 */
export default class extends Controller {
  static targets = ['editableField'];
  static values = { url: String, sheetId: Number };

  connect() {
    this.pendingSave = null;
  }

  disconnect() {
    if (this.pendingSave) {
      clearTimeout(this.pendingSave);
    }
  }

  fieldChanged(event) {
    this.lastChangedField = event.target;
    this.scheduleSave();
  }

  // --- private ---

  scheduleSave() {
    if (this.pendingSave) {
      clearTimeout(this.pendingSave);
    }
    this.pendingSave = setTimeout(() => {
      this.pendingSave = null;
      this.saveNow();
    }, 600);
  }

  async saveNow() {
    const body = { sheet_id: this.sheetIdValue, sheet: this.fieldValues() };
    await this.performSave(body, this.lastChangedField);
    this.lastChangedField = null;
  }

  async performSave(body, flashTarget) {
    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
            ?.content,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok && flashTarget) {
        this.flashSaved(flashTarget);
      } else if (!response.ok) {
        const data = await response.json();
        console.error('Billing auto-save failed:', data.errors);
      }
    } catch (error) {
      console.error('Billing auto-save error:', error);
    }
  }

  flashSaved(field) {
    if (field.closest('.amba-billing-inline-edit')) return;

    const cell = field.closest('td');
    if (!cell) return;

    cell.classList.add('amba-billing-cell-saved');
    setTimeout(() => cell.classList.remove('amba-billing-cell-saved'), 1500);
  }

  fieldValues() {
    const values = {};
    this.editableFieldTargets.forEach((field) => {
      values[field.dataset.fieldName] = field.value;
    });
    return values;
  }
}
